import React from "react";
import { useTranslation } from "react-i18next";
import { Star, MapPin, Heart } from "lucide-react";
import { formatPrice } from "@/lib/utils";
import { getImageUrl } from "@/lib/images";
import { useFavorites } from "@/hooks/useFavorites";

export default function CourtCard({ court, index = 0, distanceKm }) {
  const { t } = useTranslation();
  const { favoriteIds, toggle } = useFavorites();
  const isFav = favoriteIds.includes(court.id);
  const cover = getImageUrl(court.images?.[0], 'card');
  return (
    <div className="tactical-card group block animate-fade-up relative" style={{ animationDelay: `${index * 60}ms` }}>
      <a href={`/courts/${court.id}`} target="_blank" rel="noopener noreferrer" data-testid={`court-card-${court.id}`}>
        <div className="relative aspect-[4/3] overflow-hidden">
          <img
            src={cover}
            alt={court.name}
            className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-[1.04] transition-all duration-500"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/30 to-transparent" />
          <div className="absolute top-3 start-3 flex gap-2">
            <span className="bg-black/50 backdrop-blur border border-white/20 text-white/80 text-[10px] tracking-[0.2em] uppercase px-2 py-1 font-bold">
              {t("home.sports." + court.sport)}
            </span>
            {court.indoor ? (
              <span className="bg-black/50 backdrop-blur border border-white/20 text-white/70 text-[10px] tracking-[0.2em] uppercase px-2 py-1 font-semibold">
                {t("court.indoor")}
              </span>
            ) : (
              <span className="bg-black/50 backdrop-blur border border-white/20 text-white/70 text-[10px] tracking-[0.2em] uppercase px-2 py-1 font-semibold">
                {t("court.outdoor")}
              </span>
            )}
            {distanceKm !== undefined && (
              <span className="bg-volt text-primary-foreground text-[10px] tracking-[0.2em] uppercase px-2 py-1 font-bold self-center leading-none">
                {distanceKm} km
              </span>
            )}
          </div>
          <div className="absolute bottom-3 start-3 end-3 flex items-end justify-between">
            <div>
              <p className="font-heading text-2xl leading-none tracking-tight text-white">{court.name}</p>
              <p className="text-xs text-white/80 mt-1 flex items-center gap-1">
                <MapPin className="h-3 w-3" /> {court.city}
                {court.facility && (
                  <span className="text-white/80 ml-1 flex items-center gap-1">
                    · {court.facility.name}
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {court.rating > 0 && (
              <div className="flex items-center gap-1 text-xs font-semibold">
                <Star className="h-3 w-3 fill-volt text-volt" />
                <span>{court.rating}</span>
                <span className="text-muted-foreground/80">({court.review_count})</span>
              </div>
            )}
          </div>
        </div>
      </div>
        <div className="px-4 py-3 flex items-center justify-between border-t border-border/30">
          {(() => {
            const pricing = court.slot_pricing || {};
            const displayDuration = court.default_slot_duration || court.slot_duration || 60;
            const displayPrice = pricing[String(displayDuration)] ?? court.price_per_slot;
            return (
              <>
                <span className="text-[10px] tracking-[0.25em] uppercase text-foreground/80 font-semibold">
                  {displayDuration !== 60
                    ? t("court.per_min", { n: displayDuration })
                    : t("court.per_hour")}
                </span>
                <span className="font-heading text-3xl text-volt leading-none">
                  {formatPrice(displayPrice, court.currency)}
                </span>
              </>
            );
          })()}
        </div>
      </a>
      <button
        onClick={(e) => {
          e.preventDefault();
          toggle(court.id);
        }}
        className="absolute top-3 end-3 z-10 h-8 w-8 flex items-center justify-center bg-background/80 backdrop-blur border border-border rounded-sm hover:bg-card transition-colors"
      >
        <Heart className={`h-4 w-4 transition-colors ${isFav ? "fill-red-500 text-red-500" : "text-muted-foreground/80 hover:text-red-400"}`} />
      </button>
    </div>
  );
}
